import { Fragment, useEffect, useMemo, useState, type KeyboardEvent } from "react";
import {
  Alert, CircularProgress, Collapse, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Typography,
} from "@mui/material";
import dayjs, { Dayjs } from "dayjs";
import PageMeta from "../components/common/PageMeta";
import PageContainer from "../components/common/PageContainer";
import PageHeader from "../components/common/PageHeader";
import DashboardPaper from "../components/common/DashboardPaper";
import MetricCard from "../components/common/MetricCard";
import Badge from "../components/ui/badge/Badge";
import ActivityTrail from "../components/evidenceActivity/ActivityTrail";
import AgentsTable from "../components/evidenceActivity/AgentsTable";
import TimeBuckets from "../components/evidenceActivity/TimeBuckets";
import SortHeader from "../components/evidenceActivity/SortHeader";
import EvidenceActivityFilters, { type ActivityPreset } from "../components/evidenceActivity/EvidenceActivityFilters";
import { OUTCOME_LABELS, PROCESSOR_LABELS, RESULT_LABELS, formatDuration } from "../components/evidenceActivity/formatDuration";
import { presets } from "../components/common/dataFilterPresets";
import { useEvidenceActivity } from "../hooks/useEvidenceActivity";
import type { EvidenceActivityDir, EvidenceActivityPackageRow, EvidenceActivitySort, EvidenceActivityTrail } from "../types/evidenceActivity";

const OUTCOME_COLORS: Record<string, "success" | "warning" | "primary" | "light"> = { finished: "success", downloaded: "light", abandoned: "warning", open: "primary" };
const RESULT_COLORS: Record<string, "success" | "error"> = { won: "success", lost: "error" };

const PACKAGE_COLUMNS: { key: EvidenceActivitySort; label: string; align?: "right" }[] = [
  { key: "dispute", label: "Dispute" },
  { key: "processor", label: "Processor" },
  { key: "agent", label: "Agent" },
  { key: "finishedAt", label: "Submitted" },
  { key: "time", label: "Time", align: "right" },
  { key: "outcome", label: "Outcome" },
];

const evidenceHref = (p: EvidenceActivityPackageRow) => (p.disputeId ? `/disputes/${p.disputeId}/evidence` : `/evidence-builder/${p.bankCaseId}`);
const pct = (num: number, den: number) => (den ? `${Math.round((num / den) * 100)}%` : "—");

export default function AdminEvidenceActivity() {
  const [preset, setPreset] = useState<ActivityPreset>("30D");
  const [startDate, setStartDate] = useState<Dayjs | null>(presets["30D"].start());
  const [endDate, setEndDate] = useState<Dayjs | null>(presets["30D"].end());
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [userId, setUserId] = useState<number | "">("");
  const [processor, setProcessor] = useState("");
  const [outcome, setOutcome] = useState("");
  const [result, setResult] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [sort, setSort] = useState<EvidenceActivitySort>("time");
  const [dir, setDir] = useState<EvidenceActivityDir>("desc");
  // A new filter starts the list over on its first page, in the same render as the filter.
  const firstPage = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPage(0); };
  const { summary, error, loading, loadTrail } = useEvidenceActivity({
    from: startDate?.format("YYYY-MM-DD"), to: endDate?.format("YYYY-MM-DD"), q, userId: userId || undefined, processor, outcome, result, page: page + 1, pageSize, sort, dir,
  });
  const lastPage = summary ? Math.max(0, Math.ceil(summary.packagesTotal / summary.pageSize) - 1) : 0;
  useEffect(() => { if (summary && page > lastPage) setPage(lastPage); }, [summary, page, lastPage]);
  // A chosen agent with nothing in the new range would filter against an option the menu no longer offers.
  useEffect(() => {
    if (summary && userId !== "" && !summary.agentOptions.some((a) => a.user_id === userId)) setUserId("");
  }, [summary, userId]);
  // One report query per pause in typing, not one per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => { setQ(search.trim()); setPage(0); }, 300);
    return () => clearTimeout(timer);
  }, [search]);
  const [open, setOpen] = useState<number | null>(null);
  const [trails, setTrails] = useState<Record<number, EvidenceActivityTrail | "loading" | "error">>({});

  // The options are everyone active in the range, so picking one agent never empties the menu of the rest.
  const agentOptions = useMemo(() => (summary?.agentOptions ?? []).map((a) => ({ userId: a.user_id, name: a.name })), [summary]);

  const choosePreset = (next: ActivityPreset) => {
    setPreset(next);
    setPage(0);
    if (next !== "custom") { setStartDate(presets[next].start()); setEndDate(presets[next].end()); }
  };

  const chooseSort = (key: EvidenceActivitySort) => {
    if (sort === key) setDir(dir === "desc" ? "asc" : "desc");
    else { setSort(key); setDir(key === "agent" || key === "processor" ? "asc" : "desc"); }
    setPage(0);
  };

  const toggle = async (draftId: number) => {
    if (open === draftId) { setOpen(null); return; }
    setOpen(draftId);
    if (trails[draftId]) return;
    setTrails((t) => ({ ...t, [draftId]: "loading" }));
    try {
      const trail = await loadTrail(draftId);
      setTrails((t) => ({ ...t, [draftId]: trail }));
    } catch {
      setTrails((t) => ({ ...t, [draftId]: "error" }));
    }
  };

  const onRowKey = (draftId: number) => (e: KeyboardEvent<HTMLTableRowElement>) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); void toggle(draftId); }
  };

  // p90 is 0 when nothing in the filter finished; then nothing is slow.
  const slowFrom = summary?.tiles.p90Seconds || Infinity;

  return (
    <div>
      <PageMeta title="Chargemont | Evidence Activity" description="How long agents spend building evidence packages" />
      <PageContainer>
        <PageHeader title="Evidence activity" />
        <Typography variant="body2" color="text.secondary" sx={{ mt: -3, mb: 3 }}>
          How long agents spend building evidence packages, whether they get submitted, and how they fare.
        </Typography>

        <Stack spacing={3}>
          <EvidenceActivityFilters
            preset={preset} startDate={startDate} endDate={endDate}
            onPresetChange={choosePreset}
            onStartDateChange={(d) => { setStartDate(d); setPreset("custom"); setPage(0); }}
            onEndDateChange={(d) => { setEndDate(d); setPreset("custom"); setPage(0); }}
            agents={agentOptions} userId={userId} onUserIdChange={firstPage(setUserId)}
            processor={processor} onProcessorChange={firstPage(setProcessor)}
            outcome={outcome} onOutcomeChange={firstPage((v: string) => { setOutcome(v); if (v !== "finished") setResult(""); })}
            result={result} onResultChange={firstPage((v: string) => { setResult(v); if (v) setOutcome("finished"); })}
            search={search} onSearchChange={setSearch}
          />

          {error && <Alert severity="error">{error}</Alert>}
          {loading && !summary && <CircularProgress size={24} />}

          {summary && (
            <>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
                <MetricCard label="Submitted" value={summary.tiles.finished} note={`${summary.tiles.downloaded} downloaded only · ${summary.tiles.abandoned} abandoned`} />
                <MetricCard label="Median time to submit" value={formatDuration(summary.tiles.medianSeconds)} note={`p90 ${formatDuration(summary.tiles.p90Seconds)} · ${formatDuration(summary.tiles.totalSeconds)} in total`} />
                <MetricCard label="Win rate" value={pct(summary.tiles.won, summary.tiles.won + summary.tiles.lost)} note={`${summary.tiles.won} won · ${summary.tiles.lost} lost · ${summary.tiles.awaiting} awaiting`} />
                <MetricCard label="Pending" value={summary.tiles.open + summary.tiles.downloaded} note={`${summary.tiles.open} open · ${summary.tiles.downloaded} downloaded, not submitted`} />
              </div>

              <DashboardPaper sx={{ p: 2.5, minWidth: 0 }}>
                <AgentsTable agents={summary.agents} total={summary.total} />
                <TimeBuckets buckets={summary.buckets} />
              </DashboardPaper>

              <DashboardPaper sx={{ p: 2.5, minWidth: 0 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1.5 }}>
                  <Typography sx={{ fontSize: 15, fontWeight: 600 }}>Packages</Typography>
                  <Typography variant="caption" color="text.disabled">{summary.packagesTotal} in range</Typography>
                </Stack>
                {summary.packages.length === 0 && <Typography variant="body2" color="text.secondary">No packages in this range</Typography>}
                <TableContainer>
                  <Table size="small" aria-label="Packages">
                    <TableHead><TableRow>
                      <TableCell sx={{ width: 28, px: 0.5 }} />
                      {PACKAGE_COLUMNS.map((c) => <SortHeader key={c.key} label={c.label} active={sort === c.key} dir={dir} onClick={() => chooseSort(c.key)} align={c.align} />)}
                    </TableRow></TableHead>
                    <TableBody>
                      {summary.packages.map((p) => {
                        const isOpen = open === p.draftId;
                        return (
                          <Fragment key={p.draftId}>
                            <TableRow
                              hover tabIndex={0} role="button" aria-expanded={isOpen}
                              onClick={() => toggle(p.draftId)} onKeyDown={onRowKey(p.draftId)}
                              sx={{ cursor: "pointer", "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: -2 }, "& td": { fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", borderBottom: isOpen ? 0 : undefined, bgcolor: isOpen ? "action.selected" : undefined } }}
                            >
                              <TableCell sx={{ px: 0.5, color: "text.disabled", fontSize: 11 }}>{isOpen ? "▾" : "▸"}</TableCell>
                              <TableCell>
                                <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>{p.disputeId ? `#${p.disputeId}` : `Bank case ${p.bankCaseId}`}</Typography>
                                <Typography variant="caption" color="text.secondary">{[p.orderName && `Order ${p.orderName}`, p.companyName].filter(Boolean).join(" · ")}</Typography>
                              </TableCell>
                              <TableCell><Badge color="light" size="sm">{PROCESSOR_LABELS[p.processor] ?? p.processor}</Badge></TableCell>
                              <TableCell>
                                {p.agent}
                                {p.people > 1 && <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>{`+${p.people - 1}`}</Typography>}
                              </TableCell>
                              <TableCell>{p.finishedAt ? dayjs(p.finishedAt).format("D MMM, HH:mm") : "—"}</TableCell>
                              <TableCell align="right" sx={p.timeSeconds >= slowFrom ? { color: "warning.main", fontWeight: 500 } : undefined}>{formatDuration(p.timeSeconds)}</TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={0.75}>
                                  <Badge color={OUTCOME_COLORS[p.outcome]} size="sm">{OUTCOME_LABELS[p.outcome]}</Badge>
                                  {p.result && <Badge color={RESULT_COLORS[p.result]} size="sm">{RESULT_LABELS[p.result]}</Badge>}
                                </Stack>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell colSpan={7} sx={{ py: 0, px: 2, borderBottom: isOpen ? undefined : 0, bgcolor: isOpen ? "action.hover" : undefined }}>
                                <Collapse in={isOpen} unmountOnExit>
                                  <TrailCell state={trails[p.draftId] ?? "loading"} pkg={p} />
                                </Collapse>
                              </TableCell>
                            </TableRow>
                          </Fragment>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  rowsPerPageOptions={[25, 50, 100]}
                  count={summary.packagesTotal}
                  page={summary.page - 1}
                  rowsPerPage={summary.pageSize}
                  onPageChange={(_, next) => setPage(next)}
                  onRowsPerPageChange={(e) => { setPageSize(Number(e.target.value)); setPage(0); }}
                />
              </DashboardPaper>
            </>
          )}
        </Stack>
      </PageContainer>
    </div>
  );
}

function TrailCell({ state, pkg }: { state: EvidenceActivityTrail | "loading" | "error"; pkg: EvidenceActivityPackageRow }) {
  if (state === "error") return <Alert severity="error" sx={{ my: 1 }}>Failed to load the step trail</Alert>;
  if (state === "loading") return <CircularProgress size={18} sx={{ my: 1 }} />;
  return <ActivityTrail trail={state} pkg={pkg} evidenceHref={evidenceHref(pkg)} />;
}
